"use client"

import * as React from "react"
import { useRouter, useParams } from "next/navigation"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useCompareStore } from "@/hooks/use-compare"
import { ROUTES } from "@/lib/routes"

export function CompareTray() {
  const router = useRouter()
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"

  const plans = useCompareStore((s) => s.plans)
  const dismissed = useCompareStore((s) => s.dismissed)
  const removePlan = useCompareStore((s) => s.removePlan)
  // const clearPlans = useCompareStore((s) => s.clearPlans)
  const setDismissed = useCompareStore((s) => s.setDismissed)

  // Clear compare state when leaving the plans page (component unmount),
  // but preserve it when navigating to the compare page.
  React.useEffect(() => {
    return () => {
      const nextPath = window.location.pathname
      const comparePath = ROUTES.compare(locale)
      if (!nextPath.startsWith(comparePath)) {
        useCompareStore.getState().clearPlans()
      }
    }
  }, [locale])

  const isVisible = plans.length > 0 && !dismissed
  const canCompare = plans.length >= 2

  return (
    <>
      {/* Spacer: prevents the last plan card's bottom row (Compare checkbox)
          from being hidden behind the fixed tray */}
      {isVisible && <div className="h-20" />}

      <div
        className={cn(
          "fixed right-0 bottom-0 left-0 z-40 transition-transform duration-300",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
      >
        <div className="border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-4px_24px_rgba(0,0,0,0.10)] dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto flex max-w-5xl items-center gap-4">
            {/* Plan mini-cards */}
            <div className="flex flex-1 items-center gap-3 overflow-x-auto pb-1">
              {plans.map((plan) => (
                <div
                  key={plan.slug}
                  className="relative flex min-w-[140px] shrink-0 flex-col items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center dark:border-slate-700 dark:bg-slate-800"
                >
                  <button
                    onClick={() => removePlan(plan.slug)}
                    className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-sm transition-colors hover:border-red-300 hover:text-red-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:text-red-400"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <p className="max-w-[120px] truncate text-xs font-semibold text-slate-900 dark:text-white">
                    {plan.name}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    {plan.lifeCover}
                  </p>
                </div>
              ))}
            </div>

            {/* Count label */}
            <span className="shrink-0 text-sm font-semibold text-nova-600 dark:text-nova-400">
              {plans.length}/3 Selected
            </span>

            {/* Compare Now button */}
            <button
              onClick={() => {
                if (canCompare) router.push(ROUTES.compare(locale))
              }}
              disabled={!canCompare}
              className={cn(
                "shrink-0 cursor-pointer rounded-lg px-5 py-2 text-sm font-semibold transition-colors",
                canCompare
                  ? "bg-nova-600 text-white hover:bg-nova-700"
                  : "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
              )}
            >
              Compare now
            </button>

            {/* Clear all */}
            {/* <button
              onClick={() => clearPlans()}
              className="shrink-0 text-xs font-medium text-slate-400 transition-colors hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
            >
              Clear all
            </button> */}

            {/* Close button */}
            <button
              onClick={() => setDismissed(true)}
              className="shrink-0 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
